import {
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { AxiosError, AxiosResponse } from "axios";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Input,
  FileUpload,
} from "src/components/common/FormComponents";
import UploadProgress from "src/components/common/UploadProgress";
import MapComponent from "src/components/common/KML";
import StatusHistory from "src/components/common/StatusHistory";
import SuccessModal from "src/components/common/modal/Success";
import ViewForm from "src/components/common/ViewForm";
import ViewImages from "src/components/common/ViewImage";
import { devTypes } from "src/constants/common";
import ProjectDetailSkeleton from "src/components/common/ProjectDetailSkeleton";
import errorMessage from "src/constants/errorMessage";
import { useAppSelector } from "src/redux/store";
import { CustomStyles, getStyles } from "src/styles/theme";
import { ActivityFormType, ProjectInterface } from "src/types/common";
import {
  addProjectToIo,
  getMyProfileData,
  getProjectById,
  queryHistory,
  UpdateActivityFormType,
  updateProjectDetails,
  updateProjectStatus,
  addProjectDocument,
  deleteProjectDocument,
} from "src/utils/api/dashboard";
import {
  dateAndTimeStamp,
  downloadSheet,
  downloadS3File,
} from "src/utils/helper";
import defaultStyles, { StylesClasses } from "../../styles";
import { defaultForm } from "../defaultForm";
import Card from "src/components/common/ImageCard";
import Checkbox from "src/components/common/FormComponents/Checkbox";

type LandLeaseDetailsProps = {
  projectId: string;
  customStyles?: CustomStyles<StylesClasses>;
};

const LandLeaseDetails = ({
  projectId,
  customStyles,
}: LandLeaseDetailsProps) => {
  const styles = getStyles<StylesClasses>(defaultStyles, customStyles);
  const [projectData, setProjectData] = useState<ProjectInterface>(null);
  const [documents, setDocuments] = useState<any>();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [openModal, setOpenModal] = useState({
    open: false,
    heading: "",
    subheading: "",
    description: "",
    btnText: "",
    type: "",
    operationType: "", // 'submit', 'upload', 'delete'
  });

  const [derivedArray, setDerivedArray] = useState([]);
  // Video upload progress - only triggered by drone video uploads
  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  // Track deleted existing image IDs for update
  const [deletedImageIds, setDeletedImageIds] = useState<string[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showRaiseQueryDialog, setShowRaiseQueryDialog] = useState(false);
  const [showForwardToAdminDialog, setShowForwardToAdminDialog] =
    useState(false);
  const [showForwardToAdminSnoDialog, setShowForwardToAdminSnoDialog] =
    useState(false);
  const [pendingFormData, setPendingFormData] =
    useState<ActivityFormType | null>(null);
  const user = useAppSelector((state) => state.root.user);

  const userType = user?.data.userType;
  const role = user?.data.role;

  const { isLoading, refetch: refetchProjectData } = useQuery(
    ["projects", projectId],
    () => getProjectById(projectId),
    {
      onSuccess: (res: AxiosResponse) => {
        setProjectData(res.data);
        const groupDocTypes = _.groupBy(res?.data?.documentDetail, "type");
        setDocuments(groupDocTypes);
      },
      onError: (err: AxiosError) => {
        setProjectData(null);
      },
      refetchOnMount: true,
    }
  );

  const userId = projectData?.organizationAffiliationDetail?.userId;
  const projectDetailID = projectData?.id;

  const { data: userData, isLoading: isProjectDetailLoading } = useQuery(
    ["userDetails", userId],
    () => getMyProfileData(userId),
    {
      onSuccess: (res: AxiosResponse) => {},
      onError: (req: AxiosError) => {},
      enabled: !!userId,
    }
  );
  const { mutate: mutateIOQueryData } = useMutation(
    "query",
    ({
      projectId,
      query,
      type,
      underQuery,
    }: {
      projectId: string;
      query: string;
      type: string;
      underQuery: string;
    }) =>
      queryHistory({
        projectId,
        query,
        type,
        underQuery,
      }),
    {
      onSuccess: () => {
        setValue("query", "");
        navigate("/");
      },
      onError: () => {},
    }
  );

  const { mutate: mutateQueryForwardToAdmin } = useMutation(
    "queryForwardToAdmin",
    async ({ query }: { query: string }) => {
      await queryHistory({
        projectId,
        query,
        type: "PROJECT",
        underQuery: "UNDER_QUERY",
      });
      return updateProjectStatus(projectId, {
        status: "VERIFICATION_PENDING",
        isForwardedToAdmin: true,
      });
    },
    {
      onSuccess: () => {
        setValue("query", "");
        navigate("/");
      },
      onError: () => {},
    }
  );

  //comment
  const { mutate: submitMutate } = useMutation(
    "changeStatus",
    (data: any) => updateProjectStatus(projectId, data),
    {
      onSuccess: (res: AxiosResponse) => {
        navigate("/");
      },
      onError: (err: AxiosError) => {},
    }
  );
  useMutation(
    "AddIO",
    ({
      projectId,
      implementingOfficerId,
    }: {
      projectId: string;
      implementingOfficerId: string;
    }) => addProjectToIo(projectId, implementingOfficerId),
    {
      onSuccess: () => {
        navigate("/");
      },
      onError: () => {},
    }
  );
  const {
    control,
    formState,
    reset,
    handleSubmit,
    watch,
    setValue,
    getValues,
    setError,
    clearErrors,
    trigger,
  } = useForm<ActivityFormType>({
    defaultValues: {
      ...defaultForm,
      landFreeFromEncumbrances: true,
      landSuitableForPlantation: true,
    },
  });
  const { errors, isValid } = formState;

  useEffect(() => {
    if (!!projectData) {
      reset({
        name: "Land Based Project",
        isEditable: false,
        projectType: "LAND_LEASED",
        landTypeId: "419bff70-3735-4627-b378-269f5e02f247",
        areaUnit: "ha",
        landArea: Number(projectData?.landArea),
        address: {
          state: projectData?.address?.state,
          district: projectData?.address?.district,
          khasraNo: projectData?.address?.khasraNo,
        },
        nodalOfficer: {
          firstName: projectData?.nodalOfficer?.firstName,
          email: projectData?.nodalOfficer?.email,
          phoneNumber: projectData?.nodalOfficer?.phoneNumber,
        },
        landSpecification: {
          edaphicDetails: projectData?.landSpecification?.edaphicDetails,
          slopeAndTerrain: projectData?.landSpecification?.slopeAndTerrain,
          slope: projectData?.landSpecification?.slope,
          terrain: projectData?.landSpecification?.terrain,
          hydrologicalConditions:
            projectData?.landSpecification?.hydrologicalConditions,
          forestType: projectData?.landSpecification?.forestType,
          geologicalFeatures:
            projectData?.landSpecification?.geologicalFeatures,
          divisionId: projectData?.landSpecification?.divisionId,
          division: projectData?.landSpecification?.division,
          range: projectData?.landSpecification?.range,
          compartmentNumber: Number(
            projectData?.landSpecification?.compartmentNumber
          ),
          beat: projectData?.landSpecification?.beat,
          treeCanopyDensity: +projectData?.landSpecification?.treeCanopyDensity,
          description: projectData?.landSpecification?.description,
          grossArea: projectData?.landSpecification?.grossArea,
          circle: projectData?.landSpecification?.circleName,
          revenueBlock: projectData?.landSpecification?.revenueBlock,
          legalStatusOfLand: _.startCase(
            projectData?.landSpecification?.legalStatusOfLand
          ),
          climaticZone: projectData?.landSpecification?.climaticZone,
          accessibility: projectData?.landSpecification?.accessibility,
          soilType: projectData?.landSpecification?.soilType,
          soilDepthAndTexture:
            projectData?.landSpecification?.soilDepthAndTexture,
          soilMoistureRegime:
            projectData?.landSpecification?.soilMoistureRegime,
          soilpH: projectData?.landSpecification?.soilpH,
          distanceToNearestWaterSource:
            projectData?.landSpecification?.distanceToNearestWaterSource,
          averagRainfall: projectData?.landSpecification?.averagRainfall,
          sourceOfWater: projectData?.landSpecification?.sourceOfWater,
          typeOfVegetation: projectData?.landSpecification?.typeOfVegetation,
          existingFauna: projectData?.landSpecification?.existingFauna,
          presenceOfInvasiveSpecies:
            projectData?.landSpecification?.presenceOfInvasiveSpecies,
          invasiveSpeciesDetails:
            projectData?.landSpecification?.invasiveSpeciesDetails,
          proximityToLocalCommunities:
            projectData?.landSpecification?.proximityToLocalCommunities,
          soilCharacteristics:
            projectData?.landSpecification?.soilCharacteristics,
          communityDependency:
            projectData?.landSpecification?.communityDependency,
          anyExistingRestrictions:
            projectData?.landSpecification?.anyExistingRestrictions,
          seedlingDetails: (() => {
            try {
              const data = projectData?.landSpecification?.seedlingDetails;
              if (typeof data === "string") {
                const parsed = JSON.parse(data);
                return Array.isArray(parsed) ? parsed : [];
              }
              return Array.isArray(data) ? data : [];
            } catch {
              return [];
            }
          })(),
          siteName: projectData?.landSpecification?.siteName,
          waterSupplyLines: projectData?.landSpecification?.waterSupplyLines,
          dominantSpecies: projectData?.landSpecification?.dominantSpecies,
          rightsAndUsage: projectData?.landSpecification?.rightsAndUsage,
        },
        geoTaggedImages: projectData?.images.map((image) => ({
          file: image?.imageUrl,
          key: image?.id,
          name: image?.documentName,
          type: "default",
        })),
        type: "PROJECT",
        documentDetailForDroneVideo: projectData?.documentDetail
          ?.filter((doc) => doc?.type === "DRONE_VIDEO")
          .map((doc) => ({
            documentUrl: doc?.documentUrl ?? "",
            file:
              typeof doc?.documentUrl === "string"
                ? doc.documentUrl
                : doc?.documentUrl?.fileUrl ?? "",
            name: doc?.documentName,
            type: doc?.type,
            key: doc?.id ?? "",
          })),
        //old
        /*documentDetail: [
          {
            documentUrl: "86f743c3-62e3-44b0-97e9-7ebc2d1a47c4",
            type: "LAND_KML_FILE",
            key: "",
          },
        ],*/
      });
    }
  }, [projectData, reset]);

  const updateProject = useMutation(
    ["signUp"],
    (data: UpdateActivityFormType) => updateProjectDetails(data),
    {
      onSuccess: async (res: AxiosResponse) => {
        // Refetch the project data to get updated values
        await refetchProjectData();

        setOpenModal({
          open: true,
          heading: "",
          subheading: "",
          description: "Project Details Updated successfully",
          btnText: "",
          type: "success",
          operationType: "submit",
        });
      },
      onError: (err: AxiosError) => {},
    }
  );

  // Video upload mutation - shows upload progress during video processing
  const { mutate: addVideos } = useMutation(
    "addProjectDocuments",
    (documents: Array<{ documentUrl: string; type: string }>) => {
      // Start video upload progress - only for drone videos
      setIsVideoUploading(true);
      setVideoUploadProgress(30); // Start progress
      return Promise.all(
        documents.map((doc) => addProjectDocument(projectDetailID, doc))
      );
    },
    {
      onSuccess: async () => {
        setVideoUploadProgress(60); // Progress during fetch

        // Clear the form field to trigger re-render
        setValue("documentDetailForDroneVideo", []);
        clearErrors("documentDetailForDroneVideo");

        // Invalidate the query cache to ensure fresh data
        await queryClient.invalidateQueries(["projects", projectId]);

        setVideoUploadProgress(80); // Progress during refetch

        // Refetch project data to render uploaded videos immediately
        await refetchProjectData();

        setVideoUploadProgress(100); // Complete

        setOpenModal({
          open: true,
          heading: "",
          subheading: "",
          description: "Videos added successfully",
          btnText: "",
          type: "success",
          operationType: "upload",
        });

        // Hide video upload progress after completion
        setTimeout(() => {
          setIsVideoUploading(false);
          setVideoUploadProgress(0);
        }, 1000);
      },
      onError: (err: AxiosError) => {
        // Reset video upload progress on error
        setIsVideoUploading(false);
        setVideoUploadProgress(0);
      },
    }
  );

  const { mutate: deleteVideo } = useMutation(
    "deleteProjectDocument",
    (documentId: string) => {
      return deleteProjectDocument(projectDetailID, documentId);
    },
    {
      onSuccess: async () => {
        // Invalidate the query cache to ensure fresh data
        await queryClient.invalidateQueries(["projects", projectId]);

        // Refetch project data to update the video list
        await refetchProjectData();

        setOpenModal({
          open: true,
          heading: "",
          subheading: "",
          description: "Video deleted successfully",
          btnText: "",
          type: "success",
          operationType: "delete",
        });
      },
      onError: (err: AxiosError) => {
        setOpenModal({
          open: true,
          heading: "",
          subheading: "",
          description: "Failed to delete video",
          btnText: "",
          type: "error",
          operationType: "delete",
        });
      },
    }
  );

  const { mutate: deleteKml } = useMutation(
    "deleteKmlDocument",
    (documentId: string) => {
      return deleteProjectDocument(projectDetailID, documentId);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["projects", projectId]);
        await refetchProjectData();

        setOpenModal({
          open: true,
          heading: "",
          subheading: "",
          description: "KML file deleted successfully",
          btnText: "",
          type: "success",
          operationType: "delete",
        });
      },
      onError: (err: AxiosError) => {
        setOpenModal({
          open: true,
          heading: "",
          subheading: "",
          description: "Failed to delete KML file",
          btnText: "",
          type: "error",
          operationType: "delete",
        });
      },
    }
  );

  const handleKmlDownload = (signedUrl: string) => {
    const regNo = projectData?.registrationNo;
    const filename = regNo
      ? `plantation_block_${regNo}.kml`
      : "plantation_block.kml";
    downloadS3File(signedUrl, filename);
  };

  const { mutate: addKmlDocument } = useMutation(
    "addKmlDocument",
    (documents: Array<{ documentUrl: string; type: string }>) => {
      return Promise.all(
        documents.map((doc) => addProjectDocument(projectDetailID, doc))
      );
    },
    {
      onSuccess: async () => {
        setValue("kmlDocID", []);
        clearErrors("kmlDocID");
        await queryClient.invalidateQueries(["projects", projectId]);
        await refetchProjectData();

        setOpenModal({
          open: true,
          heading: "",
          subheading: "",
          description: "KML file uploaded successfully",
          btnText: "",
          type: "success",
          operationType: "upload",
        });
      },
      onError: (err: AxiosError) => {
        setOpenModal({
          open: true,
          heading: "",
          subheading: "",
          description: "Failed to upload KML file",
          btnText: "",
          type: "error",
          operationType: "upload",
        });
      },
    }
  );

  const droneVideoSectionRef = useRef<HTMLDivElement>(null);
  const geoTaggedImagesSectionRef = useRef<HTMLDivElement>(null);

  const [seedlingsList, setSeedlingsList] = useState<any[]>([]);

  useEffect(() => {
    const parsedSeedlings = (() => {
      try {
        const data = projectData?.landSpecification?.seedlingDetails;
        if (typeof data === "string") {
          const parsed = JSON.parse(data);
          return Array.isArray(parsed) ? parsed : [];
        }
        return Array.isArray(data) ? data : [];
      } catch {
        return [];
      }
    })();

    if (parsedSeedlings && parsedSeedlings.length > 0) {
      setSeedlingsList(parsedSeedlings);
    } else if (role === "Implementing Agency") {
      // Initialize with one empty row for editing
      setSeedlingsList([
        {
          commonName: "",
          botanicalName: "",
          numberOfSeedlings: "",
        },
      ]);
    } else {
      setSeedlingsList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectData, role]);

  const handleShowConfirmDialog = (data: ActivityFormType) => {
    const existingImages =
      projectData?.images?.filter((img) => !deletedImageIds.includes(img.id)) ??
      [];
    const pendingImages =
      data?.geoTaggedImages?.filter(
        (item) => !existingImages.some((existing) => existing.id === item.key)
      ) ?? [];

    const hasGeoTaggedImages =
      existingImages.length > 0 || pendingImages.length > 0;

    if (!hasGeoTaggedImages) {
      geoTaggedImagesSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    const existingDroneVideos =
      projectData?.documentDetail?.filter(
        (doc) => doc.type === "DRONE_VIDEO"
      ) ?? [];
    const pendingDroneVideos =
      data?.documentDetailForDroneVideo?.filter(
        (item) =>
          !existingDroneVideos.some((existing) => existing.id === item.key)
      ) ?? [];

    const hasDroneVideo =
      existingDroneVideos.length > 0 || pendingDroneVideos.length > 0;

    if (!hasDroneVideo) {
      droneVideoSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    setPendingFormData(data);
    setShowConfirmDialog(true);
  };

  const handleCancelSubmit = () => {
    setShowConfirmDialog(false);
    setPendingFormData(null);
  };

  const handleConfirmSubmit = () => {
    if (pendingFormData) {
      setShowConfirmDialog(false);
      handleFormSubmit(pendingFormData);
    }
  };

  const handleFormSubmit = (data: ActivityFormType) => {
    // Get existing image IDs from project data
    const existingImageIds = projectData?.images?.map((img) => img.id) || [];

    // Filter to only include newly added images (not in original project data)
    const newImages =
      data?.geoTaggedImages?.filter(
        (item) => !existingImageIds.includes(item.key)
      ) || [];

    // Get existing video IDs from project data
    const existingVideoIds =
      projectData?.documentDetail
        ?.filter((doc) => doc.type === "DRONE_VIDEO")
        .map((doc) => doc.id) || [];

    // Filter to only include newly added videos (not in original project data)
    const newVideos =
      data?.documentDetailForDroneVideo?.filter(
        (item) => !existingVideoIds.includes(item.key)
      ) || [];

    // Submit project data (without videos)
    updateProject.mutate({
      id: projectDetailID,
      data: {
        ...data,
        landArea: +data.landArea,
        landSpecification: {
          ...data.landSpecification,
          legalStatusOfLand: _.camelCase(
            data.landSpecification.legalStatusOfLand
          ),
          compartmentNumber: +data.landSpecification.compartmentNumber,
          treeCanopyDensity: +data.landSpecification.treeCanopyDensity,
          totalArea: +data.landArea,
          grossArea: +data?.landSpecification.grossArea,
          seedlingDetails: JSON.stringify(seedlingsList) as any,
        },
        kmlDocID: data?.kmlDocID?.[0]?.key,
        images:
          newImages.length > 0 ? newImages.map((item) => item.key) : undefined,
      },
    });

    // Add new videos via separate API endpoint
    if (newVideos.length > 0) {
      const videoDocuments = newVideos.map((video) => ({
        documentUrl: video.key,
        type: "DRONE_VIDEO",
      }));
      addVideos(videoDocuments);
    }
  };

  const handleUpdateData = (data: ActivityFormType) => {
    // Get existing image IDs from project data
    const existingImageIds = projectData?.images?.map((img) => img.id) || [];

    // Helper to extract document key from S3 signed URL
    const extractDocKey = (url: string): string => {
      try {
        const urlObj = new URL(url);
        return urlObj.pathname.split("/").pop() || url;
      } catch {
        return url;
      }
    };

    // Build all image document keys: existing (kept) + newly uploaded
    const allImageKeys =
      data?.geoTaggedImages?.map((item) => {
        if (existingImageIds.includes(item.key)) {
          // Existing image: extract raw document key from signed URL
          return extractDocKey(item.file);
        }
        // New image: key is already the document key
        return item.key;
      }) || [];

    // Get existing video IDs from project data
    const existingVideoIds =
      projectData?.documentDetail
        ?.filter((doc) => doc.type === "DRONE_VIDEO")
        .map((doc) => doc.id) || [];

    // Filter to only include newly added videos (not in original project data)
    const newVideos =
      data?.documentDetailForDroneVideo?.filter(
        (item) => !existingVideoIds.includes(item.key)
      ) || [];

    // Update project data (without videos)
    updateProject.mutate({
      id: projectDetailID,
      data: {
        ...data,
        landArea: +data.landArea,
        landSpecification: {
          ...data.landSpecification,
          legalStatusOfLand: _.camelCase(
            data.landSpecification.legalStatusOfLand
          ),
          compartmentNumber: +data.landSpecification.compartmentNumber,
          treeCanopyDensity: +data.landSpecification.treeCanopyDensity,
          totalArea: +data.landArea,
          grossArea: +data?.landSpecification.grossArea,
          circleName: data.landSpecification.circle,
          seedlingDetails: JSON.stringify(seedlingsList) as any,
        },
        kmlDocID: data?.kmlDocID?.[0]?.key,
        images: allImageKeys as any,
        editValue: true,
      },
    });

    // Add new videos via separate API endpoint
    if (newVideos.length > 0) {
      const videoDocuments = newVideos.map((video) => ({
        documentUrl: video.key,
        type: "DRONE_VIDEO",
      }));
      addVideos(videoDocuments);
    }

    // Reset deleted image tracking after update
    setDeletedImageIds([]);
  };

  const droneVideoFromObj = projectData?.documentDetail?.find(
    (doc) => doc.type === "DRONE_VIDEO"
  );
  const droneVideoSrc =
    typeof droneVideoFromObj?.documentUrl === "string"
      ? droneVideoFromObj.documentUrl
      : droneVideoFromObj?.documentUrl?.fileUrl;

  const handleResponseModal = () => {
    if (openModal.type === "success" && openModal.operationType === "submit") {
      // Only navigate for form submit operations
      navigate("/");
    } else {
      // For upload, delete, or error - stay on page
      handleModalClose();
    }
  };

  const handleModalClose = () => {
    setOpenModal({ ...openModal, open: false });
  };

  useEffect(() => {
    const extractedData = projectData?.projectQuery.map((item) => ({
      Name: item.user.firstName,
      Query: item.query,
      created_at: dateAndTimeStamp(item.created_at, "IST"),
    }));
    setDerivedArray(extractedData);
  }, [projectData]);

  const exportQuery = () => {
    downloadSheet(derivedArray);
  };

  const isApproved = projectData?.status === "APPROVED";

  const disable = !(
    ((projectData?.status === "UNDER_QUERY" ||
      projectData?.status === "NODAL_UNDER_QUERY") &&
      user.data.userType !== "INDIVIDUAL") ||
    role === "Implementing Agency"
  );

  // Allowed editable fields for Implementing Agency when status is APPROVED
  const allowedEditableFields = [
    "landSpecification.circle",
    "landSpecification.revenueBlock",
    "landSpecification.range",
    "landSpecification.compartmentNumber",
    "landSpecification.beat",
    "address.khasraNo",
    "landSpecification.legalStatusOfLand",
    "landSpecification.accessibility",
    "landSpecification.climaticZone",
    // "landSpecification.slopeAndTerrain",
    "landSpecification.slope",
    "landSpecification.terrain",
    "landSpecification.forestType",
    "landSpecification.soilType",
    "landSpecification.soilDepthAndTexture",
    "landSpecification.soilMoistureRegime",
    "landSpecification.soilpH",
    "landSpecification.sourceOfWater",
    "landSpecification.averageGroundWaterLevel",
    "landSpecification.distanceToNearestWaterSource",
    "landSpecification.averagRainfall",
    "landSpecification.typeOfVegetation",
    "landSpecification.existingFauna",
    "landSpecification.presenceOfInvasiveSpecies",
    "landSpecification.invasiveSpeciesDetails",
    "landSpecification.seedlingDetails",
    "landSpecification.nearestVillageName",
    "landSpecification.distanceToNearestHabitation",
    "landSpecification.communityDependency",
    "landSpecification.siteName",
    "landSpecification.waterSupplyLines",
    "landSpecification.dominantSpecies",
    "landSpecification.rightsAndUsage",
    "landSpecification.anyExistingRestrictions",
    "landSpecification.soilCharacteristics",
    "landSpecification.proximityToLocalCommunities",
    "landSpecification.treeCanopyDensity",
    "landArea",
    "landSpecification.description",
  ];

  const isFieldEditable = (fieldName: string): boolean => {
    // When status is APPROVED, no fields should be editable
    if (isApproved) return false;
    // Allow seedling details editing when user has role Implementing Agency
    if (fieldName === "landSpecification.seedlingDetails") {
      return !disable;
    }
    // For other fields, check if userType is null and role is Implementing Agency
    if (
      userType === null &&
      role === "Implementing Agency" &&
      (projectData?.status === "NODAL_UNDER_QUERY" ||
        projectData?.status === "UNDER_QUERY")
    ) {
      return allowedEditableFields.includes(fieldName);
    }
    return disable;
  };

  return (
    <>
      {/* Video upload progress - only shows during drone video uploads */}
      <UploadProgress
        isUploading={isVideoUploading}
        progress={videoUploadProgress}
        message="Uploading video..."
      />
      <Box {...styles("projectName")}>
        Eco-Restoration Block Registration No: {projectData?.registrationNo}
      </Box>

      <Box
        style={{ fontWeight: "bold", marginBottom: "16px", color: "#555555" }}
      >
        Status:{" "}
        {{
          NODAL_VERIFICATION_PENDING: "Verification Pending With SNO",
          NODAL_UNDER_QUERY: "Under Query with SNO",
          UNDER_QUERY: "Under Query with Admin",
          VERIFICATION_PENDING: "Verification Pending with Admin",
        }[projectData?.status] || projectData?.status.replaceAll("_", " ")}
      </Box>

      <Box component={"form"} onSubmit={handleSubmit(handleShowConfirmDialog)}>
        <Box>
          {!isLoading && !!projectData ? (
            <>
              <Box {...styles("userDetails")}>
                <Box {...styles("heading")}>
                  {userData?.data?.userType === devTypes.ORGANIZATION
                    ? "Entity Details"
                    : "Implementing Officer Details"}
                </Box>
                <Box {...styles("container")}>
                  <Box {...styles("input")}>
                    <Input
                      name="userInfo.name"
                      label={
                        userData?.data?.userType === devTypes.ORGANIZATION
                          ? "Entity Name"
                          : "Name"
                      }
                      placeholder=""
                      type="text"
                      readOnly={true}
                      value={
                        userData?.data?.userType === devTypes.ORGANIZATION
                          ? userData?.data?.firstName
                          : userData?.data?.firstName
                      }
                      control={control}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="userInfo.email"
                      label="Email"
                      placeholder=""
                      type="text"
                      readOnly={true}
                      value={userData?.data?.email}
                      control={control}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="userInfo.phoneNumber"
                      label="Contact Number"
                      placeholder=""
                      type="text"
                      readOnly={true}
                      value={`${
                        !!userData?.data?.countryCode
                          ? userData?.data?.countryCode
                          : "+91"
                      } ${userData?.data?.phoneNumber}`}
                      control={control}
                    />
                  </Box>
                </Box>
              </Box>

              <Box {...styles("userDetails")}>
                <Box {...styles("heading")}>
                  {userData?.data?.userType === devTypes.ORGANIZATION
                    ? "Entity Details"
                    : "Nodal Officer Details"}
                </Box>
                <Box {...styles("container")}>
                  <Box {...styles("input")}>
                    <Input
                      name="userInfo.name"
                      label={
                        userData?.data?.userType === devTypes.ORGANIZATION
                          ? "Entity Name"
                          : "Name"
                      }
                      placeholder=""
                      type="text"
                      readOnly={true}
                      value={
                        userData?.data?.userType === devTypes.ORGANIZATION
                          ? projectData?.nodalOfficer?.firstName
                          : projectData?.nodalOfficer?.firstName
                      }
                      control={control}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="userInfo.email"
                      label="Email"
                      placeholder=""
                      type="text"
                      readOnly={true}
                      value={projectData?.nodalOfficer?.email}
                      control={control}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="userInfo.phoneNumber"
                      label="Contact Number"
                      placeholder=""
                      type="text"
                      readOnly={true}
                      value={`${
                        !!userData?.data?.countryCode
                          ? userData?.data?.countryCode
                          : "+91"
                      } ${projectData?.nodalOfficer?.phoneNumber}`}
                      control={control}
                    />
                  </Box>
                </Box>
              </Box>

              <Box {...styles("userDetails")}>
                <Box {...styles("heading")}>
                  Proposed Eco-Restoration Block Details
                </Box>
                <Box {...styles("container")}>
                  <Box {...styles("input")}>
                    <Input
                      name="landType"
                      label="Land Type"
                      placeholder=""
                      type="text"
                      readOnly={true}
                      value={projectData?.landType}
                      control={control}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="address.state"
                      label="State"
                      placeholder=""
                      type="text"
                      readOnly={true}
                      value={projectData?.address?.stateName}
                      control={control}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="address.district"
                      label="District"
                      placeholder=""
                      type="text"
                      readOnly={true}
                      value={projectData?.address?.districtName}
                      control={control}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.divisionId"
                      label="Division"
                      placeholder=""
                      type="text"
                      readOnly={true}
                      value={projectData?.landSpecification?.division}
                      control={control}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.circle"
                      label="Circle"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      type="text"
                      readOnly={!isFieldEditable("landSpecification.circle")}
                      control={control}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.siteName"
                      label="Name of Site"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      type="text"
                      readOnly={!isFieldEditable("landSpecification.siteName")}
                      control={control}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.revenueBlock"
                      label="Revenue Block"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      type="text"
                      readOnly={
                        !isFieldEditable("landSpecification.revenueBlock")
                      }
                      control={control}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.range"
                      label="Forest Range"
                      placeholder=""
                      type="text"
                      readOnly={!isFieldEditable("landSpecification.range")}
                      control={control}
                      rules={{ required: errorMessage.required }}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.compartmentNumber"
                      label="Compartment Number"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      type="number"
                      readOnly={
                        !isFieldEditable("landSpecification.compartmentNumber")
                      }
                      control={control}
                    />
                  </Box>
                  {/* <Box {...styles("input")}>
                    <Input
                      name="landSpecification.grossArea"
                      label="Gross Area"
                      placeholder=""
                      type="number"
                      readOnly={disable}
                      control={control}
                      endAdornment={<Box>{`${projectData?.areaUnit}`}</Box>}
                    />
                  </Box> */}
                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.beat"
                      label="Beat"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      type="text"
                      readOnly={!isFieldEditable("landSpecification.beat")}
                      control={control}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="address.khasraNo"
                      label="Survey No"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      type="number"
                      readOnly={!isFieldEditable("address.khasraNo")}
                      control={control}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.treeCanopyDensity"
                      label="Tree Canopy Density"
                      placeholder=""
                      type="number"
                      readOnly={
                        !isFieldEditable("landSpecification.treeCanopyDensity")
                      }
                      control={control}
                      validationTrigger={trigger}
                      rules={{
                        max: {
                          value: 0.2,
                          message:
                            "Tree canopy density value should not be greater than 0.20",
                        },
                        validate: (value) =>
                          Number(value) >= 0 ||
                          "Tree canopy density must be a positive number",
                        required: errorMessage.required,
                      }}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="landArea"
                      label="Total Land Area"
                      placeholder=""
                      type="number"
                      readOnly={!isFieldEditable("landArea")}
                      rules={{ required: errorMessage.required }}
                      control={control}
                      endAdornment={<Box>{`${projectData?.areaUnit}`}</Box>}
                    />
                  </Box>

                  {/* todo
                  <Box {...styles("input")}>
                    <Input
                      name="occupiedArea"
                      label="Occupied Land Area"
                      placeholder=""
                      type="number"
                      readOnly={true}
                      control={control}
                      endAdornment={<Box>{`${projectData?.areaUnit}`}</Box>}
                    />
                  </Box>
                 */}
                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.slope"
                      label="Slope"
                      placeholder="e.g.: 15%"
                      rules={{ required: errorMessage.required }}
                      type="text"
                      readOnly={!isFieldEditable("landSpecification.slope")}
                      customStyles={{
                        label: defaultStyles.label,
                        wrapper: defaultStyles.selectWrapper,
                        input: defaultStyles.multilineInput,
                      }}
                      multiline
                      control={control}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.terrain"
                      label="Terrain (Slope %)"
                      placeholder="e.g.: plain, hilly"
                      rules={{ required: errorMessage.required }}
                      type="text"
                      readOnly={!isFieldEditable("landSpecification.terrain")}
                      customStyles={{
                        label: defaultStyles.label,
                        wrapper: defaultStyles.selectWrapper,
                        input: defaultStyles.multilineInput,
                      }}
                      multiline
                      control={control}
                    />
                  </Box>

                  {watch("landSpecification.slopeAndTerrain") && (
                    <Box {...styles("input")}>
                      <Input
                        name="landSpecification.slopeAndTerrain"
                        label="Slope And Terrain"
                        placeholder=""
                        customStyles={{
                          label: defaultStyles.label,
                          wrapper: defaultStyles.selectWrapper,
                          input: defaultStyles.multilineInput,
                        }}
                        multiline
                        minRows={6}
                        type="text"
                        readOnly={
                          !isFieldEditable("landSpecification.slopeAndTerrain")
                        }
                        control={control}
                      />
                    </Box>
                  )}

                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.climaticZone"
                      label="Climatic Zone"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      multiline
                      type="text"
                      readOnly={
                        !isFieldEditable("landSpecification.climaticZone")
                      }
                      control={control}
                    />
                  </Box>

                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.accessibility"
                      label="Accessibility with Distance (km)"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      type="text"
                      readOnly={
                        !isFieldEditable("landSpecification.accessibility")
                      }
                      helperText="e.g: near road, distance, approach route"
                      control={control}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.legalStatusOfLand"
                      label="Legal Status of Land"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      type="text"
                      readOnly={
                        !isFieldEditable("landSpecification.legalStatusOfLand")
                      }
                      control={control}
                    />
                  </Box>
                </Box>
              </Box>

              <Box {...styles("userDetails")}>
                <Box {...styles("heading")}>Edaphic Details</Box>

                <Box {...styles("container")}>
                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.soilType"
                      label="Soil Type"
                      rules={{ required: errorMessage.required }}
                      placeholder=""
                      multiline
                      customStyles={{
                        label: defaultStyles.label,
                        wrapper: defaultStyles.selectWrapper,
                        input: defaultStyles.multilineInput,
                      }}
                      type="text"
                      helperText="e.g. sandy, clayey, loamy, lateritic, etc."
                      readOnly={!isFieldEditable("landSpecification.soilType")}
                      control={control}
                    />
                  </Box>

                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.soilDepthAndTexture"
                      label="Soil Depth (cm) & Texture"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      type="text"
                      readOnly={
                        !isFieldEditable(
                          "landSpecification.soilDepthAndTexture"
                        )
                      }
                      customStyles={{
                        label: defaultStyles.label,
                        wrapper: defaultStyles.selectWrapper,
                        input: defaultStyles.multilineInput,
                      }}
                      multiline
                      control={control}
                    />
                  </Box>

                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.soilMoistureRegime"
                      label="Soil Moisture Regime"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      type="text"
                      customStyles={{
                        label: defaultStyles.label,
                        wrapper: defaultStyles.selectWrapper,
                        input: defaultStyles.multilineInput,
                      }}
                      multiline
                      helperText="dry, moist, seasonal waterlogged, etc"
                      readOnly={
                        !isFieldEditable("landSpecification.soilMoistureRegime")
                      }
                      control={control}
                    />
                  </Box>

                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.soilpH"
                      label="Soil pH and Fertility Status"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      type="text"
                      readOnly={!isFieldEditable("landSpecification.soilpH")}
                      control={control}
                    />
                  </Box>
                </Box>
              </Box>

              <Box {...styles("userDetails")}>
                <Box {...styles("heading")}>Hydrological Details</Box>

                <Box {...styles("container")}>
                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.sourceOfWater"
                      label="Source of Water"
                      rules={{ required: errorMessage.required }}
                      placeholder=""
                      customStyles={{
                        label: defaultStyles.label,
                        wrapper: defaultStyles.selectWrapper,
                        input: defaultStyles.multilineInput,
                      }}
                      multiline
                      type="text"
                      helperText="e.g: natural storm, bore wells, tanks, etc."
                      readOnly={
                        !isFieldEditable("landSpecification.sourceOfWater")
                      }
                      control={control}
                    />
                  </Box>

                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.waterSupplyLines"
                      label="Average Ground Water Level (meters)"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      type="text"
                      readOnly={
                        !isFieldEditable("landSpecification.waterSupplyLines")
                      }
                      control={control}
                    />
                  </Box>

                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.distanceToNearestWaterSource"
                      label="Distance to Nearest Water Source (km)"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      customStyles={{
                        label: defaultStyles.label,
                        wrapper: defaultStyles.selectWrapper,
                        input: defaultStyles.multilineInput,
                      }}
                      multiline
                      type="number"
                      readOnly={
                        !isFieldEditable(
                          "landSpecification.distanceToNearestWaterSource"
                        )
                      }
                      control={control}
                    />
                  </Box>

                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.averagRainfall"
                      label="Average rainfall in the region (milli meters)"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      type="text"
                      customStyles={{
                        label: defaultStyles.label,
                        wrapper: defaultStyles.selectWrapper,
                        input: defaultStyles.multilineInput,
                      }}
                      multiline
                      readOnly={
                        !isFieldEditable("landSpecification.averagRainfall")
                      }
                      control={control}
                    />
                  </Box>
                </Box>
              </Box>

              <Box {...styles("userDetails")}>
                <Box {...styles("heading")}>Existing Vegetation</Box>

                <Box {...styles("container")}>
                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.forestType"
                      label="Forest Type"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      customStyles={{
                        label: defaultStyles.label,
                        wrapper: defaultStyles.selectWrapper,
                        input: defaultStyles.multilineInput,
                      }}
                      multiline
                      type="text"
                      readOnly={
                        !isFieldEditable("landSpecification.forestType")
                      }
                      control={control}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.typeOfVegetation"
                      label="Type of Vegetation"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      type="text"
                      customStyles={{
                        label: defaultStyles.label,
                        wrapper: defaultStyles.selectWrapper,
                        input: defaultStyles.multilineInput,
                      }}
                      multiline
                      helperText="e.g: scrup, grassland, regenerating forest, degraded plantation, etc."
                      readOnly={
                        !isFieldEditable("landSpecification.typeOfVegetation")
                      }
                      control={control}
                    />
                  </Box>

                  {isFieldEditable("landSpecification.seedlingDetails") ||
                  (seedlingsList && seedlingsList?.length > 0) ? (
                    <Box {...styles("input")}>
                      <Box {...styles("heading")}>
                        Number of existing Trees inside land parcel
                      </Box>
                      <Box sx={{ overflowX: "auto" }}>
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            color: "#888888",
                          }}
                        >
                          <thead>
                            <tr>
                              <th style={{ textAlign: "left", padding: 8 }}>
                                Common Name
                              </th>
                              <th style={{ textAlign: "left", padding: 8 }}>
                                Botanical Name
                              </th>
                              <th style={{ textAlign: "right", padding: 8 }}>
                                Count
                              </th>
                              {isFieldEditable(
                                "landSpecification.seedlingDetails"
                              ) && (
                                <th
                                  style={{
                                    textAlign: "center",
                                    padding: 8,
                                    width: "80px",
                                  }}
                                >
                                  Action
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {seedlingsList &&
                              seedlingsList?.length > 0 &&
                              seedlingsList?.map((s, i) => {
                                const isEditable = isFieldEditable(
                                  "landSpecification.seedlingDetails"
                                );
                                return (
                                  <tr
                                    key={i}
                                    style={{
                                      color: "#888888",
                                      backgroundColor: isEditable
                                        ? "#f9f9f9"
                                        : "transparent",
                                    }}
                                  >
                                    <td
                                      style={{
                                        padding: 8,
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      <input
                                        type="text"
                                        value={s.commonName || ""}
                                        disabled={!isEditable}
                                        style={{
                                          border: isEditable
                                            ? "1px solid #ccc"
                                            : "none",
                                          padding: "4px",
                                          width: "100%",
                                          backgroundColor: isEditable
                                            ? "#fff"
                                            : "transparent",
                                          cursor: isEditable
                                            ? "text"
                                            : "default",
                                        }}
                                        onChange={(e) => {
                                          const updatedList = [
                                            ...seedlingsList,
                                          ];
                                          updatedList[i].commonName =
                                            e.target.value;
                                          setSeedlingsList(updatedList);
                                        }}
                                      />
                                    </td>
                                    <td
                                      style={{
                                        padding: 8,
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      <input
                                        type="text"
                                        value={s.botanicalName || ""}
                                        disabled={!isEditable}
                                        style={{
                                          border: isEditable
                                            ? "1px solid #ccc"
                                            : "none",
                                          padding: "4px",
                                          width: "100%",
                                          backgroundColor: isEditable
                                            ? "#fff"
                                            : "transparent",
                                          cursor: isEditable
                                            ? "text"
                                            : "default",
                                        }}
                                        onChange={(e) => {
                                          const updatedList = [
                                            ...seedlingsList,
                                          ];
                                          updatedList[i].botanicalName =
                                            e.target.value;
                                          setSeedlingsList(updatedList);
                                        }}
                                      />
                                    </td>
                                    <td
                                      style={{
                                        padding: 8,
                                        textAlign: "right",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      <input
                                        type="number"
                                        min="0"
                                        value={s.numberOfSeedlings ?? ""}
                                        disabled={
                                          !isFieldEditable(
                                            "landSpecification.seedlingDetails"
                                          )
                                        }
                                        style={{
                                          border: isFieldEditable(
                                            "landSpecification.seedlingDetails"
                                          )
                                            ? "1px solid #ccc"
                                            : "none",
                                          padding: "4px",
                                          width: "100%",
                                          backgroundColor: isFieldEditable(
                                            "landSpecification.seedlingDetails"
                                          )
                                            ? "#fff"
                                            : "transparent",
                                          cursor: isFieldEditable(
                                            "landSpecification.seedlingDetails"
                                          )
                                            ? "text"
                                            : "default",
                                          textAlign: "right",
                                        }}
                                        onChange={(e) => {
                                          const updatedList = [
                                            ...seedlingsList,
                                          ];
                                          const value = e.target.value
                                            ? parseInt(e.target.value)
                                            : null;
                                          updatedList[i].numberOfSeedlings =
                                            value !== null && value < 0
                                              ? 0
                                              : value;
                                          setSeedlingsList(updatedList);
                                        }}
                                      />
                                    </td>
                                    {isFieldEditable(
                                      "landSpecification.seedlingDetails"
                                    ) && (
                                      <td
                                        style={{
                                          padding: 8,
                                          textAlign: "center",
                                          borderBottom: "1px solid #eee",
                                        }}
                                      >
                                        {!(
                                          user.data.userType === "INDIVIDUAL"
                                        ) && (
                                          <>
                                            <button
                                              onClick={() => {
                                                const updatedList =
                                                  seedlingsList.filter(
                                                    (_, index) => index !== i
                                                  );
                                                setSeedlingsList(updatedList);
                                              }}
                                              style={{
                                                backgroundColor: "#ff6b6b",
                                                color: "#fff",
                                                border: "none",
                                                padding: "4px 8px",
                                                borderRadius: "4px",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                                fontWeight: "bold",
                                              }}
                                            >
                                              Remove
                                            </button>
                                          </>
                                        )}
                                      </td>
                                    )}
                                  </tr>
                                );
                              })}
                            {seedlingsList && seedlingsList?.length === 0 && (
                              <tr>
                                <td
                                  colSpan={
                                    isFieldEditable(
                                      "landSpecification.seedlingDetails"
                                    )
                                      ? 4
                                      : 3
                                  }
                                  style={{ padding: 8, textAlign: "center" }}
                                >
                                  No trees added yet. Click "Add Row" to add
                                  trees.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </Box>
                      {isFieldEditable("landSpecification.seedlingDetails") &&
                        !(user.data.userType === "INDIVIDUAL") && (
                          <Button
                            text="Add Row"
                            onClick={() => {
                              setSeedlingsList([
                                ...seedlingsList,
                                {
                                  commonName: "",
                                  botanicalName: "",
                                  numberOfSeedlings: null,
                                },
                              ]);
                            }}
                            customStyles={{
                              root: {
                                marginTop: "12px",
                                fontSize: "14px",
                              },
                            }}
                          />
                        )}
                    </Box>
                  ) : null}

                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.presenceOfInvasiveSpecies"
                      label="Presence of Invasive Species"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      type="text"
                      readOnly={
                        !isFieldEditable(
                          "landSpecification.presenceOfInvasiveSpecies"
                        )
                      }
                      control={control}
                    />
                  </Box>

                  {watch("landSpecification.presenceOfInvasiveSpecies") ===
                    "Yes" && (
                    <Box {...styles("input")}>
                      <Input
                        name="landSpecification.invasiveSpeciesDetails"
                        label="Invasive Species Details"
                        placeholder=""
                        rules={{ required: errorMessage.required }}
                        customStyles={{
                          label: defaultStyles.label,
                          wrapper: defaultStyles.selectWrapper,
                          input: defaultStyles.multilineInput,
                        }}
                        multiline
                        readOnly={
                          !isFieldEditable(
                            "landSpecification.invasiveSpeciesDetails"
                          )
                        }
                        control={control}
                      />
                    </Box>
                  )}

                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.existingFauna"
                      label="Existing Fauna (Native Animals, wildlife, birds etc)"
                      customStyles={{
                        label: defaultStyles.label,
                        wrapper: defaultStyles.selectWrapper,
                        input: defaultStyles.multilineInput,
                      }}
                      multiline
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      type="text"
                      readOnly={
                        !isFieldEditable("landSpecification.existingFauna")
                      }
                      control={control}
                    />
                  </Box>
                </Box>
              </Box>

              {/* <Box {...styles("input")}>
                    <Input
                      name="landSpecification.dominantSpecies"
                      label="Dominant Species (if any)"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      type="text"
                      readOnly={
                        !isFieldEditable("landSpecification.dominantSpecies")
                      }
                      control={control}
                    />
                  </Box> */}

              <Box {...styles("userDetails")}>
                <Box {...styles("heading")}>Socio-Environmental Context</Box>
                <Box {...styles("container")}>
                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.soilCharacteristics"
                      label="Name of the nearest village or habitation"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      type="text"
                      readOnly={
                        !isFieldEditable(
                          "landSpecification.soilCharacteristics"
                        )
                      }
                      control={control}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.proximityToLocalCommunities"
                      label="Distance to nearest habitation (km)"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      type="text"
                      readOnly={
                        !isFieldEditable(
                          "landSpecification.proximityToLocalCommunities"
                        )
                      }
                      control={control}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.rightsAndUsage"
                      label="Rights and Usage"
                      placeholder=""
                      rules={{ required: errorMessage.required }}
                      customStyles={{
                        label: defaultStyles.label,
                        wrapper: defaultStyles.selectWrapper,
                        input: defaultStyles.multilineInput,
                      }}
                      multiline
                      minRows={1}
                      type="text"
                      readOnly={
                        !isFieldEditable("landSpecification.rightsAndUsage")
                      }
                      control={control}
                    />
                  </Box>
                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.communityDependency"
                      label="Community Dependency"
                      customStyles={{
                        label: defaultStyles.label,
                        wrapper: defaultStyles.selectWrapper,
                        input: defaultStyles.multilineInput,
                      }}
                      placeholder=""
                      multiline
                      rules={{ required: errorMessage.required }}
                      readOnly={
                        !isFieldEditable(
                          "landSpecification.communityDependency"
                        )
                      }
                      control={control}
                    />
                  </Box>

                  <Box {...styles("input")}>
                    <Input
                      name="landSpecification.anyExistingRestrictions"
                      label="Any Existing Restrictions"
                      placeholder=""
                      type="text"
                      customStyles={{
                        label: defaultStyles.label,
                        wrapper: defaultStyles.selectWrapper,
                        input: defaultStyles.multilineInput,
                      }}
                      multiline
                      rules={{ required: errorMessage.required }}
                      readOnly={
                        !isFieldEditable(
                          "landSpecification.anyExistingRestrictions"
                        )
                      }
                      control={control}
                    />
                  </Box>
                </Box>
              </Box>

              <Box {...styles("formWrapper")}>
                <Box {...styles("heading")}>.KML File of Plantation Block</Box>

                {documents?.LAND_KML_FILE?.length > 0 ? (
                  <>
                    <Box {...styles("documentGroup")}>
                      {documents?.LAND_KML_FILE?.map((item) => (
                        <Box
                          key={item.id}
                          sx={{
                            position: "relative",
                            display: "inline-block",
                          }}
                        >
                          <ViewImages
                            fileUrl={item.documentUrl}
                            label={""}
                            onDownload={() =>
                              handleKmlDownload(item.documentUrl)
                            }
                          />
                          {userType === null &&
                            role === "Implementing Agency" &&
                            !isApproved && (
                              <Tooltip title="Delete KML File">
                                <IconButton
                                  onClick={() => deleteKml(item.id)}
                                  sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                                    "&:hover": {
                                      backgroundColor: "rgba(255, 255, 255, 1)",
                                    },
                                  }}
                                  size="small"
                                >
                                  <DeleteIcon
                                    sx={{
                                      color: "#d32f2f",
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                        </Box>
                      ))}
                    </Box>
                    {!isProjectDetailLoading && !isLoading && (
                      <MapComponent
                        kmlUrl={
                          projectData?.documentDetail?.find(
                            (doc) => doc.type === "LAND_KML_FILE"
                          )?.documentUrl
                        }
                      />
                    )}
                  </>
                ) : (
                  <Box sx={{ py: 1, color: "#888" }}>No KML file uploaded</Box>
                )}
                {userType === null &&
                  role === "Implementing Agency" &&
                  projectData &&
                  !isApproved &&
                  (!documents?.LAND_KML_FILE ||
                    documents?.LAND_KML_FILE?.length === 0) && (
                    <Box sx={{ width: "100%" }}>
                      <FileUpload
                        name="kmlDocID"
                        label="Please upload GPS Boundary (KML file) of land parcel"
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        maxLimit={1}
                        supportedFormatMessage="Please ensure that the KML file should contain closed polygon of land parcel and area of polygon in KML file should not less than the area filled in the form"
                        trigger={trigger}
                        dragDropHeading=""
                        onSuccess={(res) => {
                          const uploadedFile = res?.data?.uploadedFile;
                          if (uploadedFile?.key) {
                            addKmlDocument([
                              {
                                documentUrl: uploadedFile.key,
                                type: "LAND_KML_FILE",
                              },
                            ]);
                          }
                        }}
                      />
                    </Box>
                  )}
              </Box>

              {/* KML file upload - show when no KML exists or user can re-upload */}

              <Box {...styles("formWrapper")} ref={geoTaggedImagesSectionRef}>
                <Box {...styles("heading")}>GeoTagged Images</Box>
                <Box {...styles("imageWrapper2")}>
                  {projectData?.images.filter(
                    (img) => !deletedImageIds.includes(img.id)
                  ).length ? (
                    <>
                      {projectData?.images
                        ?.filter((img) => !deletedImageIds.includes(img.id))
                        .map((item) => (
                          <Box
                            key={item?.id}
                            sx={{
                              position: "relative",
                              display: "inline-block",
                            }}
                          >
                            <Card
                              title={`GeoTagged Image`}
                              image={item?.imageUrl}
                            />
                            {userType === null &&
                              role === "Implementing Agency" &&
                              !isApproved &&
                              !disable && (
                                <Tooltip title="Delete Image">
                                  <IconButton
                                    onClick={() => {
                                      setDeletedImageIds((prev) => [
                                        ...prev,
                                        item.id,
                                      ]);
                                      const currentImages =
                                        getValues("geoTaggedImages") || [];
                                      setValue(
                                        "geoTaggedImages",
                                        currentImages.filter(
                                          (img) => img.key !== item.id
                                        )
                                      );
                                    }}
                                    sx={{
                                      position: "absolute",
                                      top: 4,
                                      right: 4,
                                      backgroundColor: "rgba(255,255,255,0.8)",
                                      "&:hover": {
                                        backgroundColor: "rgba(255,0,0,0.1)",
                                      },
                                    }}
                                    size="small"
                                  >
                                    <DeleteIcon
                                      fontSize="small"
                                      color="error"
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}
                          </Box>
                        ))}
                    </>
                  ) : (
                    <></>
                  )}
                </Box>

                {userType === null &&
                  role === "Implementing Agency" &&
                  projectData &&
                  !isApproved && (
                    <Box sx={{ width: "100%" }}>
                      <FileUpload
                        key={watch("geoTaggedImages")[0]?.key}
                        accept="image/jpeg, image/png, image/webp, image/jpg"
                        name="geoTaggedImages"
                        label="Upload More GeoTagged Images"
                        control={control}
                        disabled={disable}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        required={false}
                        clearErrors={clearErrors}
                        maxLimit={10}
                        multiple={true}
                        supportedFormatMessage="Supported formats: .jpeg, .png, .webp, .jpg"
                        trigger={trigger}
                        dragDropHeading=""
                      />
                    </Box>
                  )}
              </Box>

              {/* drone video view*/}
              <Box {...styles("formWrapper")} ref={droneVideoSectionRef}>
                <Box {...styles("heading")}>Drone Videos</Box>
                {projectData?.documentDetail?.filter(
                  (doc) => doc.type === "DRONE_VIDEO"
                ).length > 0 ? (
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {projectData?.documentDetail
                      ?.filter((doc) => doc.type === "DRONE_VIDEO")
                      .map((video, idx) => {
                        console.log(video);
                        const videoSrc =
                          typeof video.documentUrl === "string"
                            ? video.documentUrl
                            : video.documentUrl?.fileUrl;
                        console.log(videoSrc);
                        return (
                          <Box
                            key={video.id}
                            sx={{
                              position: "relative",
                              display: "inline-block",
                            }}
                          >
                            <video
                              src={videoSrc}
                              controls
                              width="300px"
                              height="300px"
                              style={{ borderRadius: "8px" }}
                            />
                            <Box
                              title={video.documentName}
                              sx={{
                                textAlign: "center",
                                mt: 1,
                                maxWidth: "300px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {video.documentName ?? `Video ${idx + 1}`}
                            </Box>
                            {userType === null &&
                              role === "Implementing Agency" &&
                              !isApproved && (
                                <Tooltip title="Delete Video">
                                  <IconButton
                                    onClick={() => deleteVideo(video.id)}
                                    sx={{
                                      position: "absolute",
                                      top: 8,
                                      right: 8,
                                      backgroundColor:
                                        "rgba(255, 255, 255, 0.9)",
                                      "&:hover": {
                                        backgroundColor:
                                          "rgba(255, 255, 255, 1)",
                                      },
                                    }}
                                    size="small"
                                  >
                                    <DeleteIcon
                                      sx={{
                                        color: "#d32f2f",
                                      }}
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}
                          </Box>
                        );
                      })}
                  </Box>
                ) : (
                  <Box>No drone videos uploaded</Box>
                )}
                {userType === null &&
                  role === "Implementing Agency" &&
                  projectData &&
                  !isApproved && (
                    <Box sx={{ width: "100%" }}>
                      <FileUpload
                        key={
                          watch("documentDetailForDroneVideo")?.[0]?.key ||
                          "drone-video-upload"
                        }
                        accept="video/mp4, video/mov, video/avi, video/mkv"
                        name="documentDetailForDroneVideo"
                        label="Upload Drone Camera Video"
                        control={control}
                        disabled={disable}
                        errors={errors}
                        setValue={setValue}
                        getValues={getValues}
                        setError={setError}
                        clearErrors={clearErrors}
                        maxFileSize={300 * 1024 * 1024}
                        maxLimit={2}
                        multiple={true}
                        supportedFormatMessage="Supported formats: .mp4, .mov, .avi, .mkv Size Upto 200MB"
                        trigger={trigger}
                        dragDropHeading=""
                      />
                    </Box>
                  )}
              </Box>

              {/* drone video upload */}

              {projectData?.landSpecification?.description && (
                <Box {...styles("input")} sx={{ maxWidth: "70%" }}>
                  <Input
                    customStyles={{
                      label: defaultStyles.label,
                      wrapper: defaultStyles.selectWrapper,
                      input: defaultStyles.multilineInput,
                    }}
                    name="landSpecification.description"
                    rules={{
                      maxLength: {
                        value: 500,
                        message: "More then 500 characters are not allowed",
                      },
                    }}
                    control={control}
                    readOnly={!isFieldEditable("landSpecification.description")}
                    type="text"
                    placeholder="(In 500 characters)"
                    label="Additional Information"
                    multiline
                    minRows={6}
                  />
                </Box>
              )}
              <Box {...styles("formWrapper")}>
                <Box {...styles("heading")}>Forest Department Undertaking</Box>
                <Checkbox
                  name="landFreeFromEncumbrances"
                  control={control}
                  label={
                    "The land is free from all types of encumbrances and is under the management of the Union Territory/State Forest Department and suitable for eco-restoration under GCP."
                  }
                  rules={{
                    required: true,
                  }}
                  disabled
                />
                <Checkbox
                  name="landSuitableForPlantation"
                  control={control}
                  label={
                    "The information filled above are the correct in best of my knowledge."
                  }
                  rules={{
                    required: true,
                  }}
                  disabled
                />
              </Box>

              <StatusHistory records={projectData?.projectQuery} />

              {projectData?.projectQuery.length > 0 && (
                <Box {...styles("formWrapper")}>
                  <Box {...styles("infoContainer")}>
                    <Button
                      onClick={exportQuery}
                      text={""}
                      variant="outlined"
                      src="/assets/svg/export.svg"
                      customStyles={{
                        root: {
                          minWidth: "40px",
                        },
                      }}
                    />
                    <Box {...styles("heading")}> Query</Box>
                  </Box>
                  {projectData?.projectQuery.map((item) => (
                    <Box sx={{ paddingTop: "20px" }}>
                      <ViewForm
                        label={item.user.firstName}
                        value={item.query}
                        date={dateAndTimeStamp(item.created_at, "IST")}
                      />
                    </Box>
                  ))}
                </Box>
              )}
              <Box {...styles("formWrapper")}>
                <Box {...styles("heading")}> Remarks</Box>

                <Box sx={{ paddingTop: "20px" }}>
                  <ViewForm
                    label="Admin Remarks"
                    value={projectData?.remarks}
                  />
                  <ViewForm
                    label="Sno Remarks"
                    value={projectData?.snoRemarks}
                  />
                </Box>
              </Box>

              {projectData?.status === "WITHDRAWN" && (
                <Box {...styles("formWrapper")}>
                  {projectData?.comments?.[0]?.message && (
                    <Box>
                      <Box {...styles("heading")}>Withdrawal Details</Box>
                      <ViewForm
                        label="Withdrawal Remark"
                        value={projectData?.comments[0]?.message}
                        valueStyles={{ fontWeight: "400" }}
                      />
                      <ViewForm
                        label="Withdrawn By"
                        value={
                          [
                            projectData?.comments[0]?.user?.firstName,
                            projectData?.comments[0]?.user?.lastName,
                          ]
                            .filter(Boolean)
                            .join(" ") || "—"
                        }
                        valueStyles={{ fontWeight: "400" }}
                      />
                      <ViewForm
                        label="Date"
                        value={
                          projectData?.comments[0]?.created_at
                            ? dateAndTimeStamp(
                                projectData?.comments[0]?.created_at,
                                "IST"
                              )
                            : "—"
                        }
                        valueStyles={{ fontWeight: "400" }}
                      />
                    </Box>
                  )}
                </Box>
              )}

              {(projectData?.status === "UNDER_QUERY" ||
                projectData?.status === "NODAL_VERIFICATION_PENDING" ||
                projectData?.status === "NODAL_UNDER_QUERY") && (
                <Box {...styles("input")}>
                  <Input
                    customStyles={{
                      label: defaultStyles.label,
                      wrapper: defaultStyles.selectWrapper,
                      input: defaultStyles.multilineInput,
                    }}
                    name="query"
                    rules={{
                      required: errorMessage.required,
                      maxLength: {
                        value: 500,
                        message: "More then 500 characters are not allowed",
                      },
                    }}
                    control={control}
                    type="text"
                    placeholder="(In 500 characters)"
                    label="Query"
                    multiline
                    minRows={6}
                  />
                </Box>
              )}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItem: "center",
                  padding: "10px",
                }}
              >
                {(projectData?.status === "UNDER_QUERY" ||
                  projectData?.status === "NODAL_VERIFICATION_PENDING" ||
                  projectData?.status === "NODAL_UNDER_QUERY") &&
                  user.data.userType === "INDIVIDUAL" && (
                    <Button
                      text="Raise Query to DNO"
                      onClick={() => {
                        setShowRaiseQueryDialog(true);
                      }}
                      disabled={!watch("query")}
                      customStyles={{
                        root: {
                          fontSize: "16px",
                          bottom: "10px",
                        },
                      }}
                    />
                  )}

                {(projectData?.status === "NODAL_VERIFICATION_PENDING" ||
                  projectData?.status === "NODAL_UNDER_QUERY" ||
                  projectData?.status === "UNDER_QUERY") &&
                  projectData?.snoRemarks &&
                  userType === "INDIVIDUAL" && (
                    <>
                      {/*Forward To Admin query*/}
                      <Button
                        text="Forward To Admin"
                        onClick={() => {
                          if (!watch("query")) {
                            trigger("query");
                            return;
                          }
                          setShowForwardToAdminDialog(true);
                        }}
                        customStyles={{
                          root: {
                            fontSize: "16px",
                            bottom: "10px",
                          },
                        }}
                      />
                    </>
                  )}

                {/* {userType === null &&
                  role === "Implementing Agency" &&
                  projectData?.status === "NODAL_UNDER_QUERY" && (
                    <Button
                      text={"Update Data"}
                      onClick={() => {
                        handleUpdateData(getValues());
                      }}
                      disabled={false}
                      customStyles={{
                        root: {
                          fontSize: "16px",
                          bottom: "10px",
                        },
                      }}
                    />
                  )} */}

                {!disable &&
                  !isApproved &&
                  projectData?.status !== "NODAL_VERIFICATION_PENDING" &&
                  projectData?.status !== "VERIFICATION_PENDING" &&
                  projectData?.status !== "WITHDRAWN" &&
                  user.data.userType !== "INDIVIDUAL" && (
                    <>
                      <Button
                        text={"Submit"}
                        type="submit"
                        customStyles={{
                          root: {
                            fontSize: "16px",
                            bottom: "10px",
                            backgroundColor: !isValid
                              ? "error.main"
                              : "primary.main",
                            color: "secondary.main",
                          },
                        }}
                      />
                    </>
                  )}
              </Box>
              <SuccessModal
                open={openModal.open}
                heading={openModal.heading}
                subheading={openModal.subheading}
                description={openModal.description}
                btnText={openModal.btnText}
                type={openModal.type}
                onClick={() => handleResponseModal()}
                handleModalClose={() => handleModalClose()}
              ></SuccessModal>
              {/* Confirmation Dialog */}
              <Dialog
                open={showConfirmDialog}
                onClose={handleCancelSubmit}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle sx={{ fontWeight: 600 }}>
                  Confirm Land Lease Details Submission
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    fontWeight={600}
                  >
                    Are you sure you want to submit the land lease details?
                    Please review all the information before confirming this
                    action.
                  </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                  <Button
                    text="Cancel"
                    variant="outlined"
                    onClick={handleCancelSubmit}
                  />
                  <Button
                    text="Submit"
                    variant="contained"
                    onClick={handleConfirmSubmit}
                  />
                </DialogActions>
              </Dialog>
            </>
          ) : (
            <ProjectDetailSkeleton sections={[3, 3, 8]} />
          )}
        </Box>
      </Box>

      {(projectData?.status === "NODAL_VERIFICATION_PENDING" ||
        projectData?.status === "UNDER_QUERY") &&
        !projectData?.snoRemarks &&
        userType === "INDIVIDUAL" && (
          <>
            <Box {...styles("input")}>
              <Input
                customStyles={{
                  label: defaultStyles.label,
                  wrapper: defaultStyles.selectWrapper,
                }}
                name="snoremarks"
                rules={{ required: errorMessage.required }}
                control={control}
                errors={errors}
                placeholder="SNO Remarks"
                label="SNO Remarks"
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItem: "center",
              }}
            >
              <Button
                text="Reject"
                variant="outlined"
                disabled={!watch("snoremarks")}
                onClick={() => {
                  setShowRejectDialog(true);
                }}
                customStyles={{
                  root: {
                    fontSize: "18px",
                    color: "error.main",
                    borderColor: "error.main",
                  },
                }}
              />
              <Button
                text="Forward To Admin"
                type="submit"
                disabled={!watch("snoremarks")}
                onClick={() => {
                  setShowForwardToAdminSnoDialog(true);
                }}
                customStyles={{
                  root: {
                    fontSize: "16px",
                    textWrap: "noWrap",
                  },
                }}
              />
            </Box>
          </>
        )}

      {(projectData?.status === "NODAL_VERIFICATION_PENDING" ||
        projectData?.status === "UNDER_QUERY" ||
        projectData?.status === "NODAL_UNDER_QUERY") &&
        projectData?.snoRemarks &&
        userType === "INDIVIDUAL" && (
          <>
            <Box {...styles("input")}>
              <Input
                customStyles={{
                  label: defaultStyles.label,
                  wrapper: defaultStyles.selectWrapper,
                }}
                name="snoremarks"
                rules={{ required: errorMessage.required }}
                control={control}
                errors={errors}
                placeholder="SNO Remarks"
                label="Rejection Remarks"
              />
            </Box>
            <Box
              {...styles("btnWrapper")}
              sx={{ justifyContent: "flex-start" }}
            >
              <Button
                text="Reject"
                variant="outlined"
                disabled={!watch("snoremarks")}
                onClick={() => {
                  setShowRejectDialog(true);
                }}
                customStyles={{
                  root: {
                    fontSize: "18px",
                    color: "error.main",
                    borderColor: "error.main",
                  },
                }}
              />
            </Box>
          </>
        )}
      {/* Forward To Admin (SNO) Confirmation Dialog */}
      <Dialog
        open={showForwardToAdminSnoDialog}
        onClose={() => setShowForwardToAdminSnoDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Confirm Forward To Admin
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="textSecondary" fontWeight={600}>
            Are you sure you want to forward this to Admin?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            text="Cancel"
            variant="outlined"
            onClick={() => setShowForwardToAdminSnoDialog(false)}
          />
          <Button
            text="Confirm"
            variant="contained"
            onClick={() => {
              setShowForwardToAdminSnoDialog(false);
              submitMutate({
                projectId,
                status: "VERIFICATION_PENDING",
                remarks: watch("snoremarks"),
              });
            }}
          />
        </DialogActions>
      </Dialog>
      {/* Reject Confirmation Dialog */}
      <Dialog
        open={showRejectDialog}
        onClose={() => setShowRejectDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm Rejection</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="error" fontWeight={600}>
            Are you sure you want to reject this project? This action cannot be
            undone and the applicant will be notified.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            text="Cancel"
            variant="outlined"
            onClick={() => setShowRejectDialog(false)}
          />
          <Button
            text="Reject"
            variant="contained"
            onClick={() => {
              setShowRejectDialog(false);
              submitMutate({
                projectId,
                status: "REJECTED",
                remarks: watch("snoremarks"),
              });
            }}
            customStyles={{
              root: {
                backgroundColor: "error.main",
                "&:hover": { backgroundColor: "error.dark" },
              },
            }}
          />
        </DialogActions>
      </Dialog>
      {/* Forward To Admin Confirmation Dialog */}
      <Dialog
        open={showForwardToAdminDialog}
        onClose={() => setShowForwardToAdminDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Confirm Forward To Admin
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="textSecondary" fontWeight={600}>
            Are you sure you want to forward this Land Parcel to Admin?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            text="Cancel"
            variant="outlined"
            onClick={() => setShowForwardToAdminDialog(false)}
          />
          <Button
            text="Confirm"
            variant="contained"
            onClick={() => {
              setShowForwardToAdminDialog(false);
              mutateQueryForwardToAdmin({
                query: watch("query"),
              });
            }}
          />
        </DialogActions>
      </Dialog>
      {/* Raise Query to DNO Confirmation Dialog */}
      <Dialog
        open={showRaiseQueryDialog}
        onClose={() => setShowRaiseQueryDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm Raise Query</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="textSecondary" fontWeight={600}>
            Are you sure you want to raise this query to DNO(District Nodal
            Officer)?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            text="Cancel"
            variant="outlined"
            onClick={() => setShowRaiseQueryDialog(false)}
          />
          <Button
            text="Confirm"
            variant="contained"
            onClick={() => {
              setShowRaiseQueryDialog(false);
              mutateIOQueryData({
                projectId: projectId,
                query: watch("query"),
                type: "PROJECT",
                underQuery: "UNDER_QUERY",
              });
            }}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LandLeaseDetails;
